/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    // Catches a gift left over from a previous session/tab that's since fallen out of
    // eligibility (e.g. the qualifying product was removed elsewhere) before this page even
    // touches the cart.
    this.syncFreeGiftEligibility();

    // Queue the variant just added to the cart - always, even if the drawer's
    // `open` attribute already reads true here. main.js calls cartDrawer.open()
    // (which sets that attribute and steals focus via trapFocus synchronously)
    // *before* dispatching this event, so the attribute alone can't tell a
    // "settled, already open" drawer apart from one that just this instant
    // started opening. Since open() runs unconditionally on every drawer-mode
    // add, 'on:cart-drawer:after-open' below is always guaranteed to fire again
    // afterward, so it's the only safe place to announce this - once the open
    // (and its focus trap) has actually finished, ahead of the subtotal.
    document.addEventListener('on:cart:add', (evt) => {
      this.pendingRecentlyAddedTitle = evt.detail.variantTitle;
    });
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart-drawer:after-open', () => {
      if (this.pendingRecentlyAddedTitle) {
        this.recentlyAdded(this.pendingRecentlyAddedTitle);
        this.pendingRecentlyAddedTitle = null;
      }
      this.announceSubtotal();
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.syncFreeGiftEligibility();

      // If the drawer is already open (e.g. adjusting quantity in-drawer), announce
      // immediately. Otherwise, wait for 'on:cart-drawer:after-open' below - content
      // updated while the drawer is still aria-hidden wouldn't be announced.
      if (this.hasAttribute('open')) {
        this.announceSubtotal();
      }
    });
  }

  /**
   * Announces which variant was just added to the cart, via a hidden live
   * region, e.g. "Added Cue Case to cart".
   * @param {string} itemTitle - Title of the line item added.
   */
  recentlyAdded(itemTitle) {
    const liveRegion = this.querySelector('[data-recently-added]');
    if (!liveRegion || !itemTitle) return;
    liveRegion.textContent = theme.strings.recentlyAdded.replace('[ITEM]', itemTitle);
  }

  /**
   * Announces the current cart total to screen reader users via a hidden
   * live region, e.g. after a product is added to the cart.
   */
  announceSubtotal() {
    const liveRegion = this.querySelector('[data-cart-subtotal-status]');
    const total = this.querySelector('.subtotal.opposing-items .theme-money');
    if (!liveRegion || !total) return;

    liveRegion.textContent = theme.strings.cartTotalIsNow.replace('[TOTAL]', total.textContent.trim());
  }

  /**
   * Removes a previously-added free gift line if the cart no longer meets whichever discount
   * requirement (dollar threshold or required product list) unlocked it - e.g. the customer
   * removed the qualifying product, or dropped back below the threshold. Eligibility and the
   * existing gift line's key (if any) are rendered server-side into a marker element that's
   * always present, regardless of whether the gift-unlock UI itself is currently shown - see
   * sections/cart-drawer.liquid.
   */
  syncFreeGiftEligibility() {
    if (this.freeGiftRequestInFlight) return;

    const marker = this.querySelector('[data-free-gift-eligibility]');
    if (!marker) return;

    const isEligible = marker.dataset.eligible === 'true';
    const giftLineKey = marker.dataset.existingGiftLineKey;
    if (isEligible || !giftLineKey) return;

    this.freeGiftRequestInFlight = true;
    this.removeCartLine(giftLineKey)
      .then(() => {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error.message);
      })
      .finally(() => {
        this.freeGiftRequestInFlight = false;
      });
  }

  /**
   * Sets a cart line's quantity to 0 via the Ajax Cart API.
   * @param {string} lineKey - The cart line's unique key.
   * @returns {Promise} Resolves once the line has been removed.
   */
  // eslint-disable-next-line class-methods-use-this
  removeCartLine(lineKey) {
    return fetch(theme.routes.cartUpdate, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [lineKey]: 0 } })
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

window.customElements.define('cart-drawer', CartDrawer);
